import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Eye, EyeOff } from 'lucide-react';

const API = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) 
  ? 'http://localhost:8001/api' 
  : '/api';

const AdminNews = ({ user }) => {
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [alertModal, setAlertModal] = useState({ show: false, message: '', type: 'info' });
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'GENEL',
    image_url: '',
    source: '',
    published: true
  });

  const showAlert = (message, type = 'info') => setAlertModal({ show: true, message, type });

  const fetchNews = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/news`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewsList(response.data || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching news:', err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      
      if (editingId) {
        // Update existing news
        await axios.put(`${API}/admin/news/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create new news
        await axios.post(`${API}/admin/news`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      // Reset form
      setFormData({
        title: '',
        content: '',
        category: 'GENEL',
        image_url: '',
        source: '',
        published: true
      });
      setEditingId(null);
      setShowForm(false);
      
      // Refresh list
      await fetchNews();
    } catch (err) {
      console.error('Error saving news:', err);
      showAlert('Haber kaydedilemedi!', 'error');
    }
  };

  const handleEdit = (news) => {
    setFormData({
      title: news.title,
      content: news.content,
      category: news.category,
      image_url: news.image_url || '',
      source: news.source || '',
      published: news.published
    });
    setEditingId(news.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/news/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showAlert('Haber silindi!', 'success');
      await fetchNews();
    } catch (err) {
      console.error('Error deleting news:', err);
      showAlert('Haber silinemedi!', 'error');
    }
  };

  const togglePublished = async (news) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/news/${news.id}`, 
        { published: !news.published },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      await fetchNews();
    } catch (err) {
      console.error('Error toggling published:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white">📰 Haber Yönetimi</h2>
        <button
          onClick={() => {
            setShowForm(!showForm);
            if (showForm) {
              setEditingId(null);
              setFormData({
                title: '',
                content: '',
                category: 'GENEL',
                image_url: '',
                source: '',
                published: true
              });
            }
          }}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          <Plus size={20} />
          {showForm ? 'İptal' : 'Yeni Haber Ekle'}
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-gray-800 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-bold text-white mb-4">
            {editingId ? 'Haber Düzenle' : 'Yeni Haber'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Başlık</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">İçerik</label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({...formData, content: e.target.value})}
                rows={6}
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Kategori</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
                >
                  <option value="GENEL">Genel</option>
                  <option value="BORSA">Borsa</option>
                  <option value="EKONOMİ">Ekonomi</option>
                  <option value="KRİPTO">Kripto</option>
                  <option value="EMTIA">Emtia</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Kaynak</label>
                <input
                  type="text"
                  value={formData.source}
                  onChange={(e) => setFormData({...formData, source: e.target.value})}
                  placeholder="Opsiyonel"
                  className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Görsel</label>
              <div className="space-y-3">
                {/* File Upload */}
                <div className="flex items-center gap-4">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        // Convert to base64
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setFormData({...formData, image_url: reader.result});
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="flex-1 text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700 file:cursor-pointer"
                  />
                </div>
                
                {/* Or URL Input */}
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-sm">veya URL:</span>
                  <input
                    type="url"
                    value={formData.image_url?.startsWith('data:') ? '' : formData.image_url}
                    onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                    placeholder="https://..."
                    className="flex-1 bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white text-sm"
                  />
                </div>
                
                {/* Image Preview */}
                {formData.image_url && (
                  <div className="mt-2">
                    <img 
                      src={formData.image_url} 
                      alt="Önizleme" 
                      className="max-h-32 rounded-lg border border-gray-600"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.published}
                onChange={(e) => setFormData({...formData, published: e.target.checked})}
                className="w-4 h-4"
              />
              <label className="text-sm text-gray-300">Yayında</label>
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition-colors"
              >
                {editingId ? 'Güncelle' : 'Ekle'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                }}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg transition-colors"
              >
                İptal
              </button>
            </div>
          </form>
        </div>
      )}

      {/* News List */}
      <div className="space-y-4">
        {newsList.length === 0 ? (
          <div className="bg-gray-800 rounded-lg p-8 text-center text-gray-400">
            Henüz haber eklenmemiş
          </div>
        ) : (
          newsList.map((news) => (
            <div key={news.id} className="bg-gray-800 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-bold text-white">{news.title}</h3>
                    <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded">
                      {news.category}
                    </span>
                    {!news.published && (
                      <span className="text-xs bg-gray-600 text-white px-2 py-1 rounded">
                        Taslak
                      </span>
                    )}
                  </div>
                  <p className="text-gray-400 text-sm mb-2 line-clamp-2">{news.content}</p>
                  {news.source && (
                    <p className="text-xs text-gray-500">Kaynak: {news.source}</p>
                  )}
                  <p className="text-xs text-gray-600 mt-2">
                    {new Date(news.created_at).toLocaleDateString('tr-TR')}
                  </p>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => togglePublished(news)}
                    className="text-gray-400 hover:text-white transition-colors p-2"
                    title={news.published ? 'Yayından Kaldır' : 'Yayınla'}
                  >
                    {news.published ? <Eye size={20} /> : <EyeOff size={20} />}
                  </button>
                  <button
                    onClick={() => handleEdit(news)}
                    className="text-blue-400 hover:text-blue-300 transition-colors p-2"
                    title="Düzenle"
                  >
                    <Edit size={20} />
                  </button>
                  <button
                    onClick={() => handleDelete(news.id)}
                    className="text-red-400 hover:text-red-300 transition-colors p-2"
                    title="Sil"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Site İçi Alert Modal */}
      {alertModal.show && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl w-full max-w-sm p-5 shadow-2xl">
            <div className="text-center">
              <div className="text-4xl mb-3">
                {alertModal.type === 'success' ? '✅' : alertModal.type === 'error' ? '❌' : 'ℹ️'}
              </div>
              <p className={`text-sm mb-4 ${alertModal.type === 'success' ? 'text-green-400' : alertModal.type === 'error' ? 'text-red-400' : 'text-gray-300'}`}>
                {alertModal.message}
              </p>
              <button
                onClick={() => setAlertModal({ show: false, message: '', type: 'info' })}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNews;
